/**
 * EvaluationDimensionEditor Component
 *
 * Editor for a single evaluation dimension in a reviewer persona.
 * Allows configuration of:
 * - Dimension name (will be converted to snake_case)
 * - Weight (importance multiplier)
 * - Description
 * - Scoring criteria (rubric)
 */

import React from 'react';
import {
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Typography,
  Slider,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

function EvaluationDimensionEditor({
  dimension,
  index,
  onUpdate,
  onRemove,
  canRemove = true
}) {
  const handleChange = (field, value) => {
    onUpdate(index, field, value);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <DragIcon sx={{ color: 'text.disabled', mt: 1, cursor: 'grab' }} />
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              {/* Dimension Name */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Dimension Name"
                  value={dimension.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="mentalizing_stance"
                  helperText="Will be converted to snake_case"
                />
              </Grid>

              {/* Weight */}
              <Grid item xs={12} md={2}>
                <Typography variant="body2" gutterBottom>
                  Weight: {dimension.weight.toFixed(1)}x
                </Typography>
                <Slider
                  value={dimension.weight}
                  onChange={(e, v) => handleChange('weight', v)}
                  min={0.1}
                  max={3.0}
                  step={0.1}
                  marks={[
                    { value: 0.5, label: '0.5' },
                    { value: 1.0, label: '1.0' },
                    { value: 2.0, label: '2.0' },
                    { value: 3.0, label: '3.0' }
                  ]}
                  size="small"
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Max Score */}
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max"
                  type="number"
                  value={dimension.maxScore ?? 3}
                  onChange={(e) => handleChange('maxScore', Math.min(10, Math.max(1, parseInt(e.target.value) || 3)))}
                  inputProps={{ min: 1, max: 10 }}
                  helperText="0–N"
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={dimension.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of what this evaluates"
                />
              </Grid>

              {/* Scoring Criteria */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Scoring Criteria"
                  value={dimension.scoringCriteria}
                  onChange={(e) => handleChange('scoringCriteria', e.target.value)}
                  placeholder="0=Absent, 1=Basic, 2=Good, 3=Excellent"
                  helperText="Define the scoring rubric (0-3 scale recommended)"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Remove Button */}
          <Tooltip title={canRemove ? 'Remove dimension' : 'At least one dimension required'}>
            <span>
              <IconButton
                onClick={() => onRemove(index)}
                disabled={!canRemove}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

export default EvaluationDimensionEditor;
